<script setup lang="ts">
/**
 * "Request access" — opened from a locked Hub tile.
 *
 * Says in one sentence why the assistant is locked (the backend's wording,
 * from the same admittance check the chat applies), takes an optional
 * reason, and files a request. Asking again while a request is open is not
 * an error: the backend returns the existing one and the dialog says when it
 * was made.
 *
 * Nothing here grants access. An admin handles the request by hand, and the
 * tile unlocks on its own once they have — the Hub reads admittance live.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  AccessRequestError,
  requestAccess,
  shortDate,
  type CatalogAssistant,
} from "../api/accessRequests";

const MAX_REASON = 500;

const props = defineProps<{
  assistant: CatalogAssistant | null;
  devUser?: string;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  /** A request exists now (new or already open) — the Hub updates the tile. */
  (e: "requested", slug: string, createdAt: string | null, id: number): void;
}>();

const reason = ref("");
const busy = ref(false);
const errorMsg = ref<string | null>(null);
/** Set after submitting: the text of the confirmation shown in place of the form. */
const done = ref<string | null>(null);
const reasonRef = ref<HTMLTextAreaElement | null>(null);

const openRequestDate = computed(() =>
  props.assistant?.request_status === "open"
    ? shortDate(props.assistant.request?.created_at)
    : "",
);
const lastOutcome = computed(() => {
  const s = props.assistant?.request_status;
  if (!s || s === "open") return "";
  const when = shortDate(props.assistant?.request?.handled_at);
  const word =
    s === "rejected" ? "declined" : s === "granted" ? "granted" : "handled";
  return `Your last request was ${word}${when ? ` on ${when}` : ""}.`;
});

function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.preventDefault();
    close();
  }
}

watch(
  () => props.assistant,
  (a) => {
    if (a) {
      reason.value = "";
      errorMsg.value = null;
      done.value = null;
      document.addEventListener("keydown", onKeydown);
      nextTick(() => reasonRef.value?.focus());
    } else {
      document.removeEventListener("keydown", onKeydown);
    }
  },
);
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));

function close(): void {
  emit("close");
}

async function submit(): Promise<void> {
  const a = props.assistant;
  if (!a || busy.value) return;
  busy.value = true;
  errorMsg.value = null;
  try {
    const { request, created } = await requestAccess(
      a.slug,
      reason.value.trim(),
      props.devUser,
    );
    const when = shortDate(request.created_at);
    done.value = created
      ? "Request sent. An administrator will look at it and get back to you."
      : `You already requested access${when ? ` on ${when}` : ""}. It is still open.`;
    emit("requested", a.slug, request.created_at, request.id);
  } catch (e) {
    errorMsg.value =
      e instanceof AccessRequestError && e.status === 409
        ? "You already have access — reload the page to open it."
        : e instanceof Error
          ? e.message
          : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="assistant" class="dialog__backdrop" @click.self="close">
      <div
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accreq-title"
      >
        <header class="dialog__header">
          <h2 id="accreq-title">{{ assistant.name }}</h2>
          <button
            type="button"
            class="dialog__close"
            aria-label="Close"
            @click="close"
          >
            ×
          </button>
        </header>

        <section class="dialog__body">
          <p class="why">{{ assistant.blocked_reason }}</p>

          <template v-if="done">
            <p class="success">{{ done }}</p>
            <div class="dialog__actions">
              <button class="btn btn--primary" @click="close">Done</button>
            </div>
          </template>

          <template v-else-if="openRequestDate">
            <p class="success">
              You already requested access on {{ openRequestDate }}. It is still
              open.
            </p>
            <div class="dialog__actions">
              <button class="btn btn--primary" @click="close">OK</button>
            </div>
          </template>

          <form v-else class="dialog__form" @submit.prevent="submit">
            <p v-if="lastOutcome" class="hint">{{ lastOutcome }}</p>
            <label class="field">
              <span class="field__label">Why do you need it? (optional)</span>
              <textarea
                ref="reasonRef"
                v-model="reason"
                class="field__input"
                rows="3"
                :maxlength="MAX_REASON"
                placeholder="E.g. I am covering for a colleague in this team."
              />
              <span class="field__count">
                {{ reason.length }} / {{ MAX_REASON }}
              </span>
            </label>
            <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
            <div class="dialog__actions">
              <button
                type="button"
                class="btn btn--secondary"
                :disabled="busy"
                @click="close"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn--primary" :disabled="busy">
                {{ busy ? "Sending…" : "Request access" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.dialog__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 29, 46, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: var(--space-4);
}
.dialog {
  background: var(--color-surface);
  border-radius: var(--radius-lg, 12px);
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 48px rgba(15, 29, 46, 0.3);
}
.dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.dialog__header h2 {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
  color: var(--color-ink);
}
.dialog__close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--color-muted);
  cursor: pointer;
  padding: 0 0.4rem;
}
.dialog__close:hover {
  color: var(--color-ink);
}
.dialog__body {
  padding: var(--space-4) var(--space-5);
}
.why {
  margin: 0 0 var(--space-3);
  color: var(--color-ink);
  line-height: 1.5;
}
.hint {
  margin: 0;
  color: var(--color-muted);
  font-size: 0.88rem;
}
.success {
  background: var(--color-accent-dim);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  margin: 0;
  color: var(--color-ink);
}
.dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-ink);
}
.field__input {
  font-family: inherit;
  font-size: 0.95rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  width: 100%;
  resize: vertical;
}
.field__input:focus {
  outline: 2px solid var(--color-accent);
  outline-offset: -1px;
  border-color: var(--color-accent);
}
.field__count {
  align-self: flex-end;
  font-size: 0.75rem;
  color: var(--color-muted);
}
.error {
  color: var(--color-error);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.7rem;
  border-radius: var(--radius-sm);
  margin: 0;
  font-size: 0.9rem;
}
.dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-3);
}
.btn {
  font: inherit;
  padding: 0.55rem 1.2rem;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
}
.btn--primary {
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
}
.btn--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}
.btn--primary:disabled {
  background: var(--color-border);
  color: var(--color-muted);
  cursor: not-allowed;
}
.btn--secondary {
  background: transparent;
  border-color: var(--color-border);
  color: var(--color-ink);
}
.btn--secondary:hover:not(:disabled) {
  background: var(--color-surface-alt);
}
</style>
